import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, useAbruf, type ApiFehler } from '../api'
import { Angabe, Fehleranzeige, Karte, Merkmal, Tabelle, Zustand } from '../bausteine'
import { datum, geld } from '../formate'
import { Statusmerkmal } from './Vertraege'

interface Vertragszeile {
  id: string
  vertragsnummer: string
  marktlokation: string | null
  status: string
  lieferbeginn: string
  lieferende: string | null
}

interface Kundenakte {
  id: string
  kundennummer: string
  kundenart: string
  anrede: string | null
  vorname: string | null
  nachname: string | null
  firmenname: string | null
  geburtsdatum: string | null
  rechnungsanschrift: {
    strasse: string
    hausnummer: string
    postleitzahl: string
    ort: string
  } | null
  email: string | null
  telefon: string | null
  iban: string | null
  kontoinhaber: string | null
  werbeeinwilligung: boolean
  erechnungZustimmung: boolean
  gesperrt: boolean
  sperrgrund: string | null
  vertraege: Vertragszeile[]
}

interface Konto {
  saldo: string
  offenePosten: { belegnummer: string; rueckstand: string; faelligAm: string }[]
}

export default function Kundenakte() {
  const { kundennummer } = useParams()
  const akte = useAbruf<Kundenakte>(`/api/kunden/${kundennummer}`)
  const konto = useAbruf<Konto>(
    akte.daten ? `/api/debitoren/konto/${akte.daten.id}` : undefined,
    [akte.daten?.id],
  )

  const [email, setzeEmail] = useState<string>()
  const [telefon, setzeTelefon] = useState<string>()
  const [meldung, setzeMeldung] = useState<string>()
  const [fehler, setzeFehler] = useState<ApiFehler>()

  async function kontaktSpeichern() {
    setzeMeldung(undefined)
    setzeFehler(undefined)
    try {
      await api.aendere(`/api/kunden/${kundennummer}/kontakt`, {
        email: email ?? akte.daten?.email,
        telefon: telefon ?? akte.daten?.telefon,
      })
      setzeMeldung('Die Kontaktdaten wurden gespeichert.')
      akte.erneut()
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler)
    }
  }

  return (
    <>
      <h1>{akte.daten?.firmenname ?? `${akte.daten?.vorname ?? ''} ${akte.daten?.nachname ?? ''}`}</h1>
      <p className="untertitel">Kundennummer {kundennummer}</p>

      <Zustand laeuft={akte.laeuft} fehler={akte.fehler}>
        {akte.daten && (
          <>
            {akte.daten.gesperrt && (
              <div className="hinweis fehler">
                <strong>Der Kunde ist gesperrt.</strong>
                <div>{akte.daten.sperrgrund ?? 'Kein Grund hinterlegt.'}</div>
              </div>
            )}

            <Karte titel="Stammdaten">
              <div className="angaben">
                <Angabe beschriftung="Art">
                  {akte.daten.kundenart === 'PRIVATKUNDE' ? 'Privatkunde' : 'Gewerbekunde'}
                </Angabe>
                <Angabe beschriftung="Geburtsdatum">{datum(akte.daten.geburtsdatum)}</Angabe>
                <Angabe beschriftung="Rechnungsanschrift">
                  {akte.daten.rechnungsanschrift
                    ? `${akte.daten.rechnungsanschrift.strasse} ${akte.daten.rechnungsanschrift.hausnummer}, ${akte.daten.rechnungsanschrift.postleitzahl} ${akte.daten.rechnungsanschrift.ort}`
                    : '—'}
                </Angabe>
                <Angabe beschriftung="Bankverbindung">
                  {akte.daten.iban ? `${akte.daten.iban} (${akte.daten.kontoinhaber})` : '—'}
                </Angabe>
                <Angabe beschriftung="Werbeeinwilligung">
                  {akte.daten.werbeeinwilligung ? (
                    <Merkmal art="gut">Erteilt</Merkmal>
                  ) : (
                    <Merkmal>Nicht erteilt</Merkmal>
                  )}
                </Angabe>
                <Angabe beschriftung="Elektronische Rechnung">
                  {akte.daten.erechnungZustimmung ? (
                    <Merkmal art="gut">Zugestimmt</Merkmal>
                  ) : (
                    <Merkmal>Nicht zugestimmt</Merkmal>
                  )}
                </Angabe>
              </div>
            </Karte>

            <Karte titel="Kontaktdaten">
              {meldung && <div className="hinweis info">{meldung}</div>}
              {fehler && <Fehleranzeige fehler={fehler} />}
              <div className="leiste" style={{ marginBottom: 0 }}>
                <div className="feld">
                  <label htmlFor="email">E-Mail</label>
                  <input
                    id="email"
                    style={{ width: 260 }}
                    defaultValue={akte.daten.email ?? ''}
                    onChange={(ereignis) => setzeEmail(ereignis.target.value)}
                  />
                </div>
                <div className="feld">
                  <label htmlFor="telefon">Telefon</label>
                  <input
                    id="telefon"
                    style={{ width: 200 }}
                    defaultValue={akte.daten.telefon ?? ''}
                    onChange={(ereignis) => setzeTelefon(ereignis.target.value)}
                  />
                </div>
                <button className="betont" onClick={kontaktSpeichern}>
                  Speichern
                </button>
              </div>
            </Karte>

            <Karte titel="Verträge">
              <Zustand laeuft={false} leer={akte.daten.vertraege.length === 0}>
                <Tabelle
                  spalten={[
                    { name: 'Vertragsnummer' },
                    { name: 'Marktlokation' },
                    { name: 'Status' },
                    { name: 'Lieferbeginn' },
                    { name: 'Lieferende' },
                  ]}
                >
                  {akte.daten.vertraege.map((vertrag) => (
                    <tr key={vertrag.id}>
                      <td>
                        <Link to={`/vertraege/${vertrag.vertragsnummer}`}>
                          {vertrag.vertragsnummer}
                        </Link>
                      </td>
                      <td>{vertrag.marktlokation ?? '—'}</td>
                      <td>
                        <Statusmerkmal status={vertrag.status} />
                      </td>
                      <td>{datum(vertrag.lieferbeginn)}</td>
                      <td>{datum(vertrag.lieferende)}</td>
                    </tr>
                  ))}
                </Tabelle>
              </Zustand>
            </Karte>

            <Karte titel="Debitorenkonto">
              <Zustand laeuft={konto.laeuft} fehler={konto.fehler}>
                {konto.daten && (
                  <>
                    <div className="angaben" style={{ marginBottom: 14 }}>
                      <Angabe beschriftung="Saldo">{geld(konto.daten.saldo)}</Angabe>
                      <Angabe beschriftung="Offene Posten">
                        {konto.daten.offenePosten.length}
                      </Angabe>
                    </div>
                    {konto.daten.offenePosten.length > 0 && (
                      <Tabelle
                        spalten={[
                          { name: 'Belegnummer' },
                          { name: 'Fällig am' },
                          { name: 'Rückstand', zahl: true },
                        ]}
                      >
                        {konto.daten.offenePosten.map((posten) => (
                          <tr key={posten.belegnummer}>
                            <td>{posten.belegnummer}</td>
                            <td>{datum(posten.faelligAm)}</td>
                            <td className="zahl">{geld(posten.rueckstand)}</td>
                          </tr>
                        ))}
                      </Tabelle>
                    )}
                  </>
                )}
              </Zustand>
            </Karte>
          </>
        )}
      </Zustand>
    </>
  )
}
